// ==========================================
// CUSTOM WEB COMPONENT: <box-builder>
// ==========================================
if (!customElements.get('box-builder')) {

  const BoxBuilder = class extends HTMLElement {
    constructor() {
      super();
      this.items = [];
      this.dragSrcIndex = null;
      this.max = parseInt(this.dataset.max) || 8;
      this.BASE_BOX_PRICE = 30.00;

      const boxPosDataElement = document.getElementById('box-pos-data');
      const boxPosData = boxPosDataElement ? JSON.parse(boxPosDataElement.textContent) : [];
      this.boxConfigurations = boxPosData;

      // Support for Shopify's lazy loading if it exists, otherwise init immediately
      if (typeof window.initLazyScript === 'function') {
        window.initLazyScript(this, this.init.bind(this));
      } else {
        setTimeout(() => this.init(), 0);
      }
    }

    init() {
      // DOM Binding
      this.liveBoxBg = this.querySelector('#live-box-bg');
      this.selectedBox = 3;
      this.overlaysContainer = this.querySelector('#live-box-overlays');
      this.selectedItemsSummary = this.querySelector('#selected-items-summary');
      this.slotListContainer = this.querySelector('#slot-list');
      this.progressText = this.querySelector('#progress-text');
      this.itemsCountSummary = this.querySelector('#items-count-summary');
      this.itemsTotalPrice = this.querySelector('#items-total-price');
      this.grandTotalPrice = this.querySelector('#grand-total-price');
      this.btnAddToCart = this.querySelector('#btn-add-to-cart');
      this.btnIcon = this.querySelector('#btn-icon');
      this.checkoutHint = this.querySelector('#checkout-hint');

      // Bind Box Size Selector Buttons
      this.sizeBtns = this.querySelectorAll('.size-btn');
      this.sizeBtns.forEach(btn => {
        btn.addEventListener('click', (e) => this.setBoxSize(parseInt(e.currentTarget.dataset.size)));
      });

      // Bind global 'addBoxItem' event to add products from the external product list
      this.addBoxItemHandler = (e) => this.addItem(e.detail);
      document.addEventListener('addBoxItem', this.addBoxItemHandler);

      // Bind global 'selectBox' event to select box from the external box selection
      this.selectBoxHandler = (e) => {
        this.setBoxSize(parseInt(e.detail));
        this.scrollToBoxBuilder();
      }
      document.addEventListener('selectBox', this.selectBoxHandler);
      this.btnAddToCart.addEventListener('click', (e) => this.addToCart(e));

      // Setup Mobile Sticky Pill & Modal logic
      this.setupMobilePill();

      // Force initialization to max size
      this.setBoxSize(this.max);
    }

    setupMobilePill() {
      this.mobilePill = document.getElementById('mobile-cta-pill');
      this.btnShowBox = document.getElementById('btn-mobile-show-box');
      this.btnCheckout = document.getElementById('btn-mobile-checkout');
      this.mobilePillCount = document.getElementById('mobile-pill-count');
      this.mobileCheckoutIcon = document.getElementById('mobile-checkout-icon');

      if (!this.mobilePill) return;

      // Smart visibility toggling for the pill
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (window.innerWidth < 1024) { // Only active on mobile/tablet (Tailwind 'lg' breakpoint)
            // If the box builder is completely scrolled out of view upwards
            if (!entry.isIntersecting && entry.boundingClientRect.bottom < 0) {
              this.mobilePill.classList.remove('translate-y-40');
              this.mobilePill.classList.add('translate-y-0');
            } else {
              this.mobilePill.classList.add('translate-y-40');
              this.mobilePill.classList.remove('translate-y-0');
            }
          } else {
            // Ensure hidden on desktop resizing
            this.mobilePill.classList.add('translate-y-40');
            this.mobilePill.classList.remove('translate-y-0');
          }
        });
      }, { threshold: 0 }); // Fire immediately as soon as it passes boundary

      observer.observe(this);

      // Scroll handlers for the pill buttons
      if (this.btnShowBox) {
        this.btnShowBox.addEventListener('click', () => {
          this.showMobilePreview();
        });
      }

      if (this.btnCheckout) {
        this.btnCheckout.addEventListener('click', () => {
          const summaryArea = document.getElementById('box-builder-summary');
          if (summaryArea) {
            const headerOffset = document.querySelector('sticky-header') ? document.querySelector('sticky-header').offsetHeight : 80;
            window.scrollTo({
              top: summaryArea.getBoundingClientRect().top + window.pageYOffset - headerOffset - 20,
              behavior: 'smooth'
            });
          }
        });
      }

      // Hook up modal close buttons
      const btnClosePreview = document.getElementById('btn-close-preview');
      const btnClosePreviewBottom = document.getElementById('btn-close-preview-bottom');
      const modal = document.getElementById('mobile-box-preview-modal');
      
      if (btnClosePreview) btnClosePreview.addEventListener('click', () => this.hideMobilePreview());
      if (btnClosePreviewBottom) btnClosePreviewBottom.addEventListener('click', () => this.hideMobilePreview());
      if (modal) {
        modal.addEventListener('click', (e) => {
          if (e.target === modal) this.hideMobilePreview();
        });
      }
      
      if (window.lucide) window.lucide.createIcons();
    }

    updateMobilePreviewContent() {
      const content = document.getElementById('mobile-box-preview-content');
      const liveBox = document.querySelector('#live-box-container .relative.w-full');
      
      if (!content || !liveBox) return;

      // Clone the live box perfectly
      content.innerHTML = '';
      const clone = liveBox.cloneNode(true);
      clone.id = 'live-box-container-clone';
      
      // Setup interactions for cloned slots
      const slots = clone.querySelectorAll('.slot-item');
      slots.forEach((slot, index) => {
        if (index < this.items.length) {
          // Re-enable pointer events for filled slots
          slot.style.pointerEvents = 'auto'; 
          slot.classList.replace('cursor-grab', 'cursor-pointer');
          slot.classList.remove('active:cursor-grabbing');
          
          // Hide the remove overlay in preview panel so the product image is clearly visible
          const removeOverlay = slot.querySelector('.remove-overlay');
          if (removeOverlay) {
            removeOverlay.style.display = 'none';
          }

          // Click to remove logic
          slot.addEventListener('click', (e) => {
            e.stopPropagation();
            e.preventDefault();
            this.removeItem(index);
          });
        } else {
          // Empty slots shouldn't be clickable in preview
          slot.style.pointerEvents = 'none'; 
        }
      });
      
      content.appendChild(clone);
      
      // Update subtitle string
      const subtitle = document.getElementById('mobile-preview-subtitle');
      if (subtitle) {
          subtitle.textContent = window.boxBuilderStrings.mobilePreviewSubtitle
            .replace('{{ current }}', this.items.length)
            .replace('{{ max }}', this.max);
      }

      // Toggle reminder visibility
      const reminder = document.getElementById('mobile-preview-reminder');
      if (reminder) {
        reminder.style.display = this.items.length > 0 ? 'flex' : 'none';
      }
    }

    showMobilePreview() {
      const modal = document.getElementById('mobile-box-preview-modal');
      const sheet = document.getElementById('mobile-box-preview-sheet');
      
      if (!modal || !sheet) return;

      // Populate content dynamically
      this.updateMobilePreviewContent();

      // Animate Modal In
      modal.classList.remove('pointer-events-none');
      modal.classList.replace('opacity-0', 'opacity-100');
      sheet.classList.replace('translate-y-full', 'translate-y-0');
    }

    hideMobilePreview() {
      const modal = document.getElementById('mobile-box-preview-modal');
      const sheet = document.getElementById('mobile-box-preview-sheet');
      if (modal && sheet) {
        modal.classList.replace('opacity-100', 'opacity-0');
        sheet.classList.replace('translate-y-0', 'translate-y-full');
        setTimeout(() => modal.classList.add('pointer-events-none'), 300);
      }
    }

    addToCart(e) {
      // Security check: Don't proceed if the box isn't full
      if (this.items.length < this.max) return;

      const btn = this.btnAddToCart;
      const originalText = btn.innerHTML;
      
      // Loading State
      btn.disabled = true;
      btn.innerHTML = `
        <svg class="animate-spin -ml-1 mr-2 h-5 w-5 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span>${window.boxBuilderStrings.adding}</span>
      `;

      // 1. Generate unique product group ID
      const productGroupId = Math.random().toString(36).substring(2, 9);

      // 2. Format payload for selected sub-items (No aggregation, preserve index)
      const itemsPayload = this.items.map((item, index) => ({
        id: item.id,
        quantity: 1,
        properties: {
          '_productGroupId': productGroupId,
          '_subItem': 'true',
          '_index': index
        }
      }));

      // 3. Format payload for the box itself
      const boxConfig = this.boxConfigurations[this.selectedBox];
      if (boxConfig) {
        itemsPayload.push({
          id: boxConfig.id,
          quantity: 1,
          properties: {
            '_productGroupId': productGroupId
          }
        });
      }

      // Determine Shopify Subdirectory / locale routes if any
      let rootUrl = '/';
      if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
        rootUrl = window.Shopify.routes.root;
      }
      const endpoint = `${rootUrl === '/' ? '' : rootUrl}/cart/add.js`;

      // 4. Fire AJAX Add to Cart
      fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: itemsPayload,
          // Request sections to update the cart notification/drawer without redirecting
          sections: 'cart-notification-product,cart-notification-button,cart-icon-bubble',
          sections_url: window.location.pathname
        })
      })
      .then(response => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.json();
      })
      .then(res => {
        // Revert button state
        btn.innerHTML = originalText;
        btn.disabled = false;

        // Reset Box Builder State
        this.items = [];
        this.render();

        // Trigger Dawn/Craft theme Cart Notification or Drawer
        const cartNotification = document.querySelector('cart-notification');
        const cartDrawer = document.querySelector('cart-drawer');

        if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
           cartDrawer.renderContents(res);
        } else if (cartNotification && typeof cartNotification.renderContents === 'function') {
           // Dawn's cart-notification expects a single item in the `res` object. 
           // Since we added multiple items (array), we pass the primary box object, 
           // but attach the sections so Dawn can update the UI correctly without throwing JS errors.
           const boxItem = res.items.find(item => String(item.id) === String(boxConfig.id)) || res.items[0];
           boxItem.sections = res.sections;
           cartNotification.renderContents(boxItem);
        } else {
           // Fallback if neither component exists
           window.location.href = rootUrl + (rootUrl.endsWith('/') ? 'cart' : '/cart');
        }
      })
      .catch((error) => {
        console.error('Error adding to cart:', error);
        btn.innerHTML = originalText;
        btn.disabled = false;
        alert(window.boxBuilderStrings.errorAddingToCart);
      });
    }

    disconnectedCallback() {
      document.removeEventListener('addBoxItem', this.addBoxItemHandler);
    }

    scrollToBoxBuilder() {
      const boxBuilderSection = this;
      // Find the sticky header to offset the height
      const header = document.querySelector('sticky-header') || document.querySelector('.section-header');
      if (boxBuilderSection) {
        // Determine header height. Default to a generous 80px if calculation fails for any reason
        const headerHeight = header ? header.offsetHeight : 80;
        
        // Calculate where we need to scroll to
        const elementPosition = boxBuilderSection.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerHeight - 20;

        // Smoothly scroll to the calculated position
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }

    setBoxSize(size) {
      this.max = this.selectedBox = size;
      // Update toggle buttons styling
      this.sizeBtns.forEach(btn => {
        if (parseInt(btn.dataset.size) === size) {
          btn.className = "size-btn flex-1 py-2 text-sm font-semibold rounded-md transition-all bg-white shadow text-brand-green";
        } else {
          btn.className = "size-btn flex-1 py-2 text-sm font-semibold rounded-md transition-all text-gray-500 hover:text-gray-800";
        }
      });

      // Trim items if reducing size
      if (this.items.length > this.max) {
        this.items = this.items.slice(0, this.max);
      }

      this.render();
    }

    addItem(productData) {
      if (this.items.length >= this.max) {
        if (this.checkoutHint) {
          this.checkoutHint.textContent = window.boxBuilderStrings.boxFullHint;
          this.checkoutHint.classList.add('text-red-500');
          setTimeout(() => {
            this.checkoutHint.classList.remove('text-red-500');
            this.updateSummary();
          }, 2000);
        }
        return;
      }
      this.items.push(productData);
      this.render();
    }

    removeItem(index) {
      this.items.splice(index, 1);
      this.render();
    }

    // --- Drag and Drop Handlers ---
    handleDragStart(e, index) {
      this.dragSrcIndex = index;
      e.target.classList.add('opacity-50', 'scale-95');
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', index);
    }

    handleDragOver(e) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      e.currentTarget.classList.add('bg-white/30', 'backdrop-blur-sm', 'border-2', 'border-brand-green');
    }

    handleDragLeave(e) {
      e.currentTarget.classList.remove('bg-white/30', 'backdrop-blur-sm', 'border-2', 'border-brand-green');
    }

    handleDrop(e, targetIndex) {
      e.preventDefault();
      e.currentTarget.classList.remove('bg-white/30', 'backdrop-blur-sm', 'border-2', 'border-brand-green');
      if (this.dragSrcIndex !== null && this.dragSrcIndex !== targetIndex) {
        const itemToMove = this.items.splice(this.dragSrcIndex, 1)[0];
        const newIndex = Math.min(targetIndex, this.items.length);
        this.items.splice(newIndex, 0, itemToMove);
      }
      this.dragSrcIndex = null;
      this.render();
    }

    handleDragEnd(e) {
      e.target.classList.remove('opacity-50', 'scale-95');
      this.dragSrcIndex = null;
      this.render();
    }

    render() {
      if (!this.liveBoxBg || !this.overlaysContainer || !this.slotListContainer) return;

      const config = this.boxConfigurations[this.max];
      this.liveBoxBg.src = config.bg;

      this.overlaysContainer.innerHTML = '';
      this.slotListContainer.innerHTML = '';

      for (let i = 0; i < this.max; i++) {
        // 1. Render Overlay Slot (Visual Box)
        const slotConfig = config.slots[i];
        const overlay = document.createElement('div');
        overlay.className = 'absolute flex items-center justify-center overflow-hidden p-1.5 sm:p-2 transition-all duration-200 rounded-sm slot-item';
        overlay.style.top = slotConfig.top;
        overlay.style.left = slotConfig.left;
        overlay.style.width = slotConfig.width;
        overlay.style.height = slotConfig.height;

        // Drag and Drop listeners
        overlay.addEventListener('dragover', (e) => this.handleDragOver(e));
        overlay.addEventListener('dragleave', (e) => this.handleDragLeave(e));
        overlay.addEventListener('drop', (e) => this.handleDrop(e, i));

        if (i < this.items.length) {
          const item = this.items[i];
          overlay.draggable = true;
          overlay.addEventListener('dragstart', (e) => this.handleDragStart(e, i));
          overlay.addEventListener('dragend', (e) => this.handleDragEnd(e));
          overlay.classList.add('cursor-grab', 'active:cursor-grabbing', 'z-20');

          // Handle img or image keys from data
          const imgSrc = item.box || item.image;

          overlay.innerHTML = `
            <img src="${imgSrc}" class="w-full h-full object-cover rounded shadow-[0_4px_12px_rgba(0,0,0,0.15)] bg-white pointer-events-none animate-pop-in">
            <button class="remove-overlay absolute inset-0 bg-black/40 opacity-0 flex flex-col items-center justify-center text-white backdrop-blur-[2px] transition-opacity cursor-pointer z-10">
              <i data-lucide="x" class="w-6 h-6 mb-1"></i><span>${window.boxBuilderStrings.remove}</span>
            </button>
          `;

          overlay.querySelector('.remove-overlay').addEventListener('click', (e) => {
            e.stopPropagation();
            this.removeItem(i);
          });
        } else {
          // Empty drop zone
          overlay.classList.add('border-2', 'border-transparent', 'hover:border-white/50', 'hover:bg-white/10', 'z-10');
        }
        this.overlaysContainer.appendChild(overlay);

        // 2. Render List Item (Below Box)
        const listItem = document.createElement('div');
        listItem.className = 'flex items-center justify-between p-2 bg-white border border-gray-100 rounded-lg shadow-sm';

        if (i < this.items.length) {
          const item = this.items[i];
          const imgSrc = item.img || item.image;
          const title = item.title || item.name;

          listItem.innerHTML = `
            <div class="flex items-center gap-3">
              <img src="${imgSrc}" class="w-10 h-10 object-cover rounded-md border border-gray-100">
              <span class="text-sm font-semibold text-gray-800">${title}</span>
            </div>
            <button class="remove-list-btn text-gray-400 hover:text-red-500 transition-colors p-1" data-index="${i}">
              <i data-lucide="x" class="w-4 h-4"></i>
            </button>
          `;

          listItem.querySelector('.remove-list-btn').addEventListener('click', (e) => {
            this.removeItem(parseInt(e.currentTarget.dataset.index));
          });
        } else {
          listItem.classList.add('opacity-50', 'bg-transparent', 'border-dashed');
          listItem.innerHTML = `
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 rounded-md border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400">
                <span class="text-xs font-bold">${i + 1}</span>
              </div>
              <span class="text-sm font-medium text-gray-400 italic">Empty Slot</span>
            </div><span>${window.boxBuilderStrings.emptySlot}</span>
          `;
        }
        this.slotListContainer.appendChild(listItem);
      }

      // Update cloned preview modal if it's currently visible
      const modal = document.getElementById('mobile-box-preview-modal');
      if (modal && !modal.classList.contains('pointer-events-none')) {
         this.updateMobilePreviewContent();
      }

      if (window.lucide) window.lucide.createIcons();
      this.updateSummary();
    }

    updateSummary() {
      if (!this.progressText) return;

      const isFull = this.items.length === this.max;
      const remaining = this.max - this.items.length;

      this.progressText.textContent = window.boxBuilderStrings.selectedCount
        .replace('{{ current }}', this.items.length)
        .replace('{{ max }}', this.max);
      
      const itemsCountHtml = `<span id="items-count-summary">${this.items.length}</span>`;
      this.selectedItemsSummary.innerHTML = window.boxBuilderStrings.selectedItems.replace('{{ count }}', itemsCountHtml);

      // Calculate Price
      const itemsSum = this.items.reduce((acc, item) => acc + (parseFloat(item.price) || 0), 0);
      const grandTotal = this.BASE_BOX_PRICE + itemsSum;

      this.itemsTotalPrice.textContent = `${itemsSum.toFixed(2)} HKD`;
      this.grandTotalPrice.textContent = `${grandTotal.toFixed(2)} HKD`;

      // Update Desktop Checkout Button
      if (isFull) {
        this.btnAddToCart.disabled = false;
        this.btnAddToCart.classList.remove('bg-brand-green');
        this.btnAddToCart.classList.add('bg-gray-900');
        this.checkoutHint.innerHTML = `<span class="text-brand-green font-bold">${window.boxBuilderStrings.boxComplete}</span>`;
        this.btnIcon.setAttribute('data-lucide', 'check');
      } else {
        this.btnAddToCart.disabled = true;
        this.btnAddToCart.classList.add('bg-brand-green');
        this.btnAddToCart.classList.remove('bg-gray-900');
        this.checkoutHint.textContent = window.boxBuilderStrings.completeBoxHint
          .replace('{{ remaining }}', remaining)
          .replace('{{ plural_s }}', remaining !== 1 ? 's' : '');
        this.btnIcon.setAttribute('data-lucide', 'lock');
      }

      // Update Mobile Pill UI
      if (this.mobilePillCount) {
        this.mobilePillCount.textContent = this.items.length;
        
        if (isFull) {
          this.mobilePillCount.classList.replace('bg-brand-green', 'bg-gray-500');
          if (this.mobileCheckoutIcon) {
            this.mobileCheckoutIcon.setAttribute('data-lucide', 'check-circle-2');
            this.mobileCheckoutIcon.classList.replace('text-brand-green', 'text-white');
          }
          if (this.btnCheckout) {
            // Apply green state to the Checkout CTA side of the pill
            this.btnCheckout.classList.add('bg-brand-green', 'hover:bg-brand-dark');
            this.btnCheckout.classList.remove('hover:bg-white/10');
          }
        } else {
          this.mobilePillCount.classList.replace('bg-gray-500', 'bg-brand-green');
          if (this.mobileCheckoutIcon) {
            this.mobileCheckoutIcon.setAttribute('data-lucide', 'arrow-down');
            this.mobileCheckoutIcon.classList.replace('text-white', 'text-brand-green');
          }
          if (this.btnCheckout) {
            // Revert back to standard glassmorphism UI
            this.btnCheckout.classList.remove('bg-brand-green', 'hover:bg-brand-dark');
            this.btnCheckout.classList.add('hover:bg-white/10');
          }
        }
      }

      if (window.lucide) window.lucide.createIcons();
    }
  };

  window.customElements.define('box-builder', BoxBuilder);
}

// ==========================================
// GLOBAL SCRIPT: Render Products & Handle Filters
// ==========================================

const productDataElement = document.getElementById('product-data');
const productsAll = productDataElement ? JSON.parse(productDataElement.textContent) : [];
// const productsAll = products.concat(products2);

function renderProducts(filterCategory = 'All Items') {
  const productList = document.getElementById('product-list');
  if (!productList) return;

  productList.innerHTML = '';

  const filteredProducts = filterCategory === 'All Items'
    ? productsAll.filter(p => p.available === 'true')
    : productsAll.filter(p => p.category === filterCategory && p.available === 'true');

  filteredProducts.forEach(product => {
    const card = document.createElement('div');
    card.className = 'group cursor-pointer flex flex-col';
    
    // We removed the hover overlay entirely and replaced it with a hidden "Added" overlay
    card.innerHTML = `
      <div class="relative bg-white rounded-xl overflow-hidden aspect-square mb-3 shadow-sm border border-gray-100 group-hover:shadow-md transition-shadow">
        <img src="${product.img}" alt="${product.title}" class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500">
        
        <!-- Added Notification Overlay -->
        <div class="added-overlay absolute inset-0 bg-white/60 backdrop-blur-[2px] opacity-0 flex items-center justify-center transition-opacity duration-300 pointer-events-none z-10">
          <div class="added-badge bg-brand-green text-white font-bold uppercase text-sm tracking-wider px-6 py-3 rounded-full shadow-lg transform translate-y-4 opacity-0 transition-all duration-300 flex items-center gap-2">
            <i data-lucide="check" class="w-4 h-4"></i> ${window.boxBuilderStrings.added}
          </div>
        </div>
      </div>
      <h3 class="font-heading font-bold text-gray-800 text-lg leading-tight mb-1">${product.title}</h3>
      <p class="text-brand-green font-medium">${product.price} HKD</p>
    `;

    // Attach the click listener directly to the entire card
    card.addEventListener('click', () => {
      // 1. Check if the Box is already full by grabbing the state of the component
      const boxBuilder = document.querySelector('box-builder');
      const isFull = boxBuilder && boxBuilder.items.length >= boxBuilder.max;

      // 2. Dispatch the event to add the item to the box
      document.dispatchEvent(new CustomEvent('addBoxItem', { detail: product }));

      // 3. Trigger the appropriate animation
      const overlay = card.querySelector('.added-overlay');
      const badge = card.querySelector('.added-badge');

      if (isFull) {
        // Change to "Box is full" with red background
        badge.innerHTML = `<i data-lucide="alert-circle" class="w-4 h-4"></i> ${window.boxBuilderStrings.boxIsFull}`;
        badge.classList.remove('bg-brand-green');
        badge.classList.add('bg-red-500');
      } else {
        // Change to standard "Added"
        badge.innerHTML = `<i data-lucide="check" class="w-4 h-4"></i> ${window.boxBuilderStrings.added}`;
        badge.classList.remove('bg-red-500');
        badge.classList.add('bg-brand-green');
      }

      // Re-initialize any new Lucide icons created in the innerHTML
      if (window.lucide) window.lucide.createIcons();

      // Show overlay and pop the badge up
      overlay.classList.replace('opacity-0', 'opacity-100');
      badge.classList.replace('translate-y-4', 'translate-y-0');
      badge.classList.replace('opacity-0', 'opacity-100');

      // Revert the animation after a short delay (e.g., 800ms)
      setTimeout(() => {
        overlay.classList.replace('opacity-100', 'opacity-0');
        badge.classList.replace('translate-y-0', 'translate-y-4');
        badge.classList.replace('opacity-100', 'opacity-0');
      }, 800);
    });

    productList.appendChild(card);
  });

  if (window.lucide) window.lucide.createIcons();
}

// Initialize Filters
const filterBtns = document.querySelectorAll('.filter-btn');
filterBtns.forEach(btn => {
  btn.addEventListener('click', (e) => {
    const category = e.currentTarget.dataset.category;

    // Update active states
    filterBtns.forEach(b => {
      if (b.dataset.category === category) {
        b.className = 'filter-btn whitespace-nowrap px-1 py-2 text-brand-green border-b-2 border-brand-green font-semibold transition-all';
      } else {
        b.className = 'filter-btn whitespace-nowrap px-1 py-2 text-gray-500 hover:text-brand-dark font-medium border-b-2 border-transparent transition-all';
      }
    });

    renderProducts(category);
  });
});

// Initial render
renderProducts();